import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Input, Select, message } from 'antd';
import * as S from '../styles';
import { useBatchJob } from '../../../../../context/BatchJobContext';
import { getCommunicationsByName } from '../../../../../utils/services/channelAPI';
import { getCommunicationLanguages } from '../../../../../utils/services/documentAPI';
import { debounce } from 'underscore';
import { useTranslation } from 'react-i18next';
import { COMMUNICATION_GENERATOR } from '../../../../TemplateSection/translationValues';
import { BATCH_JOBS_DASHBOARD as trans } from '../translationValues';
import _ from 'underscore';
import { LoaderContainer } from '../../../../TemplateSection/styles';
import Loader from '../../../../../components/Loader';
import ScrollContainerV2 from '../../../../../components/ScrollContainerV2';
import { getAllSupportedLanguages } from '../../../../../utils/services/languageApi';
import moment from 'moment';
import { updateBatchInfo } from '../../../../../utils/services/statusMonitoringAPI';

const NAMESPACES = ['COMMUNICATION_GENERATOR', 'BATCH_JOBS_DASHBOARD'];

const LIMIT = 6;

const Step2TemplateSelection = () => {
  const { t } = useTranslation(NAMESPACES);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    jobType,
    selectedTemplate,
    setSelectedTemplate,
    searchQuery,
    setSearchQuery,
    selectedLanguage,
    setSelectedLanguage,
    languageSearchQuery,
    setLanguageSearchQuery,
    filteredLanguages,
    setFilteredLanguages,
    showLanguageDropdown,
    setShowLanguageDropdown,
    format,
    fetchedTemplates,
    setFetchedTemplates,
    page,
    setPage,
    languages,
    setLanguages,
    dataCount,
    setDataCount,
    initialFetchRef,
    setUploadedFile,
    setSelectedTemplateId,
    batchId,
    draftBatchInfo,
  } = useBatchJob();

  const { language } = draftBatchInfo ?? {};


  const hasMoreData = LIMIT * page < dataCount;

  useEffect(() => {
    if (!initialFetchRef.current) {
      return;
    }
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    setIsLoading(true);

    const apiRequests = [
      getAllSupportedLanguages(),
      getCommunicationsByName({ page: 1, limit: LIMIT, channelType: format.includes('MULTI')?'multiChannel':format.toLowerCase() }),
    ];
    const results = await Promise.allSettled(apiRequests);
    const [langResponse, templateResponse] = results;
    if (langResponse.status === 'fulfilled' && langResponse.value?.statusCode === 109) {
      setLanguages(langResponse.value?.data);
    }
    const response = templateResponse?.value;
    if (templateResponse.status === 'fulfilled' && response?.statusCode === 3210) {
      setFetchedTemplates(response?.data || []);
      initialFetchRef.current = false;
      setDataCount(response?.dataCount);
      setPage(1);
    }

    if(selectedTemplate && !filteredLanguages.length && !format.includes('MULTI')) {
      handleTemplateChange({ communicationId: selectedTemplate }, true, langResponse.value?.data);
    }
    setIsLoading(false);
  };

  const fetchMoreTemplates = async () => {
    if (isLoading || isLoadingMore || !hasMoreData) return;
    setIsLoadingMore(true);
    const response = await getCommunicationsByName({
      page: page + 1,
      limit: 6,
      ...(searchQuery && { name: searchQuery }),
      channelType: format.includes('MULTI') ? 'multiChannel' : format.toLowerCase(),
    });
    if (response?.statusCode === 3210) {
      if (response?.data) {
        setFetchedTemplates(prev => [...prev, ...response.data]);
        setDataCount(response?.dataCount);
        setPage(page + 1);
      }
    }
    setIsLoadingMore(false);
  };

  const onCommunicationsSearch = useCallback(async value => {
    setIsLoading(true);
    try {
      const response = await getCommunicationsByName({
        page: 1,
        limit: LIMIT,
        name: value,
        ...{ channelType: format.toLowerCase().includes('multi')? 'multiChannel': format.toLowerCase() },
      });
      if (response?.statusCode === 3210 && response?.data) {
          setFetchedTemplates(response?.data);
          setDataCount(response?.dataCount);
          setPage(1);      
      } else {
        setFetchedTemplates([]);
        message.error(
          t(
            COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.key,
            COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.value,
          ),
        );
      }
    } catch (e) {
      setFetchedTemplates([]);
      message.error(
        t(
          COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.key,
          COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.value,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(debounce(onCommunicationsSearch, 500), []);

  const channelKeyMap = {
    PRINT: 'printLanguages',
    EMAIL: 'emailLanguages',
    SMS: 'smsLanguages',
    CALL: 'smsLanguages',
  };

  const templateKeyMap = {
    PRINT:'printMap',
    EMAIL:'emailMap',
    SMS: 'smsMap'
  }

  const handleTemplateChange = async (templateInfo, isDraftInitialCall = false, langResponse = []) => {
    try {
      const response = await getCommunicationLanguages(templateInfo.communicationId);
      if (response?.statusCode === 3212) {
        const data = response?.data;
        let templateLanguages = [];
        let templateMap = {};
        const key = channelKeyMap[format];
        const key1 = templateKeyMap[format];
        templateLanguages = data?.[key] || [];
        templateMap = data?.[key1] || {};
        const languageList = (languages.length > 0 ? languages : langResponse)
          .filter(ln => templateLanguages?.includes(ln.code))
          .map(ln => ({
            ...ln,
            templateId: templateMap[ln.code] || null
          }));
        setFilteredLanguages(languageList);
        
        if (!isDraftInitialCall) {
          setSelectedLanguage(languageList[0] || null);
          if(jobType === 'ADVANCED') {
            try {
              await updateBatchInfo({
                batchId,
                communicationId: templateInfo.communicationId,
                language: languageList[0]?.code,
              });
            } catch (e) {
              console.error(e);
            }
          }
        } else {
          setSelectedLanguage(languageList.find(ln => ln.code === language) || null);
        }
      }
      else {
        message.error(
          t(
            COMMUNICATION_GENERATOR.FAILED_FINDING_COMMUNICATION_INFO.key,
            COMMUNICATION_GENERATOR.FAILED_FINDING_COMMUNICATION_INFO.value,
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLanguageChange = async lang => {
    const selected = filteredLanguages.find(l => l.code === lang);
    setSelectedLanguage(selected);

    if (jobType === 'ADVANCED') {
      try {
        await updateBatchInfo({
          batchId,
          communicationId: selectedTemplate,
          language: lang,
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  const saveMultiChannelTemplateInfo = async communicationId => {
    try {
      await updateBatchInfo({
        batchId,
        communicationId: communicationId,
      });
    } catch (e) {
      console.error(e);
    }
  }

  const languageSelect=()=>{
    return(
    <>
        <S.Label>
          {t(trans.SELECT_LANGUAGE_LABEL.key, trans.SELECT_LANGUAGE_LABEL.value)}{' '}
          <S.Required>*</S.Required>
        </S.Label>
        <div className="custom-language-select" style={{ position: 'relative' }}>
          <Icon
            type="search"
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              color: '#bfbfbf',
              fontSize: '16px',
              pointerEvents: 'none',
            }}
          />
          <Select
            dropdownClassName="custom-language-dropdown"
            placeholder={t(
              trans.SELECT_LANGUAGE_PLACEHOLDER.key,
              trans.SELECT_LANGUAGE_PLACEHOLDER.value,
            )}
            value={selectedLanguage ? selectedLanguage.code : undefined}
            onChange={handleLanguageChange}
            style={{ width: '100%' }}
            size="large"
            showSearch
            optionFilterProp="children"
            onDropdownVisibleChange={open => setIsDropdownOpen(open)}
            suffixIcon={
              selectedLanguage && !isDropdownOpen ? (
                <div
                  style={{
                    backgroundColor: '#0092b8',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: 600,
                    marginLeft: '8px',
                  }}
                >
                  <Icon type="check" />{' '}
                  {t(trans.SELECTED_BADGE.key, trans.SELECTED_BADGE.value)}
                </div>
              ) : (
                <Icon type={isDropdownOpen ? 'up' : 'down'} />
              )
            }
            dropdownMatchSelectWidth={true}
            disabled={!selectedTemplate}
          >
            {selectedTemplate &&
              filteredLanguages.map(l => (
                <Select.Option key={l.code} value={l.code}>
                  {l.name}
                </Select.Option>
              ))}
          </Select>
          <style>{`
              .custom-language-dropdown .ant-select-dropdown-menu-item {
                padding: 12px 16px !important;
              }
              .custom-language-dropdown .ant-select-dropdown-menu-item-active,
              .custom-language-dropdown .ant-select-dropdown-menu-item:hover {
                background-color: #ebfaee !important;
              }
              .custom-language-dropdown .ant-select-dropdown-menu::-webkit-scrollbar {
                width: 14px;
                background: #1f1f1f;
              }
              .custom-language-dropdown .ant-select-dropdown-menu::-webkit-scrollbar-thumb {
                background-color: #6b7280;
                border-radius: 10px;
                border: 3px solid #1f1f1f;
              }
              .custom-language-dropdown {
                border-radius: 12px !important;
                overflow: hidden;
              }
              .custom-language-select .ant-select-selection {
                padding-left: 30px !important;
                border-radius: 12px !important;
                height: 48px !important;
                display: flex;
                align-items: center;
                border: 2px solid ${selectedLanguage ? '#0092b8' : '#d9d9d9'} !important;
                background: ${selectedLanguage ? '#e0f7fa' : 'white'} !important;
                box-shadow: none !important;
              }
              .custom-language-select .ant-select-selection__rendered {
                line-height: normal;
                margin-left: 0;
                width: 100%;
              }
              .custom-language-select .ant-select-selection__placeholder, 
              .custom-language-select .ant-select-selection-selected-value {
                margin-left: 0;
                color: ${selectedLanguage ? '#006064' : '#bfbfbf'};
              }
              .custom-language-select .ant-select-arrow {
                top: 50%;
                right: 12px;
                width: auto;
                height: auto;
                transform: translateY(-45%);
                margin-top: 0 !important;
              }
            `}</style>
        </div>
     </>
    );
  }

  if (jobType === 'FORM') {
    return (
      <S.StepContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <S.FormContainer
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 0,
            width: '100%',
          }}
          isStepTwo={true}
        >
          <S.StepHeader>
            <S.StepHeaderTitle>
              {t(trans.LANGUAGE_AND_TEMPLATE_TITLE.key, trans.LANGUAGE_AND_TEMPLATE_TITLE.value)}
            </S.StepHeaderTitle>
            <S.StepSubtitle>
              {t(
                trans.LANGUAGE_AND_TEMPLATE_SUBTITLE.key,
                trans.LANGUAGE_AND_TEMPLATE_SUBTITLE.value,
              )}
            </S.StepSubtitle>
          </S.StepHeader>

          <div style={{ marginBottom: '16px', flexShrink: 0 }}>
            <S.Label>
              {t(trans.SEARCH_SELECT_TEMPLATE_LABEL.key, trans.SEARCH_SELECT_TEMPLATE_LABEL.value)}{' '}
              <S.Required>*</S.Required>
            </S.Label>
            <Input
              placeholder={t(
                trans.SEARCH_TEMPLATES_PLACEHOLDER.key,
                trans.SEARCH_TEMPLATES_PLACEHOLDER.value,
              )}
              value={searchQuery}
              onChange={e => {
                const value = e.target.value;
                setSearchQuery(value); // Update state immediately
                debouncedSearch(value); // Debounced search
              }}
              prefix={<Icon type="search" />}
              suffix={isLoadingMore && <Icon type="loading" />}
              size="large"
            />
            {!isLoading && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px' }}>
                <S.StepSubtitle>
                  {dataCount} {t(trans.TEMPLATES_AVAILABLE.key, trans.TEMPLATES_AVAILABLE.value)}
                </S.StepSubtitle>
                {selectedTemplate && (
                  <p style={{ color: '#0092b8' }}>
                    {t(trans.TEMPLATE_SELECTED.key, trans.TEMPLATE_SELECTED.value)}{' '}
                    <Icon type="check" />
                  </p>
                )}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', marginBottom: '16px' }}>
            <ScrollContainerV2 hideScrollToTop={true} fetchMoreData={fetchMoreTemplates}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingBottom: '16px',
                }}
              >
                {isLoading ? (
                  <LoaderContainer>
                    <Loader />
                  </LoaderContainer>
                ) : (
                  fetchedTemplates.map(tData => (
                    <S.TemplateCard
                      key={tData.communicationId}
                      selected={selectedTemplate === tData.communicationId}
                      onClick={() => {
                        setSelectedTemplate(tData.communicationId);
                        handleTemplateChange(tData);
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600' }}>{tData.communicationName}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {t(trans.CREATED_ON.key, trans.CREATED_ON.value)} {moment(tData.createdOn).format('YYYY-MM-DD')}
                        </div>
                      </div>
                      <S.CheckCircle selected={selectedTemplate === tData.communicationId}>
                        {selectedTemplate === tData.communicationId && (
                          <Icon type="check" style={{ color: '#fff', fontSize: '10px' }} />
                        )}
                      </S.CheckCircle>
                    </S.TemplateCard>
                  ))
                )}
                {!isLoading && hasMoreData && (
                  <div style={{ textAlign: 'center', paddingBlock: '10px' }}>
                    <Icon type="loading" /> {t(trans.LOADING_MORE.key, trans.LOADING_MORE.value)}
                  </div>
                )}
              </div>
            </ScrollContainerV2>
          </div>

          <div
            style={{
              marginTop: 'auto',
              borderTop: '1px solid #f3f4f6',
              backgroundColor: '#ffffff',
              flexShrink: 0,
            }}
          >
            {languageSelect()}
          </div>
        </S.FormContainer>
      </S.StepContent>
    );
  } else if (jobType === 'STANDARD' || jobType === 'ADVANCED') {
    let templateChannels;
    if(format === 'MULTI CHANNEL' && selectedTemplate) {
      templateChannels = fetchedTemplates.find(template => template.communicationId === selectedTemplate)?.availableChannels;
    }
    return (
      <S.StepContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <S.FormContainer
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 0,
            width: '100%',
          }}
        >
          <S.StepHeader>
            <S.StepHeaderTitle>
              {t(trans.CHOOSE_TEMPLATE_TITLE.key, trans.CHOOSE_TEMPLATE_TITLE.value)}
            </S.StepHeaderTitle>
            <S.StepSubtitle>
              {t(trans.CHOOSE_TEMPLATE_SUBTITLE.key, trans.CHOOSE_TEMPLATE_SUBTITLE.value)}
            </S.StepSubtitle>
          </S.StepHeader>
          <S.SearchWrapper>
            <Input
              placeholder={t(
                trans.SEARCH_TEMPLATES_PLACEHOLDER.key,
                trans.SEARCH_TEMPLATES_PLACEHOLDER.value,
              )}
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                debouncedSearch(e.target.value)
              }}
              prefix={<Icon type="search" />}
              size="large"
            />
          </S.SearchWrapper>
          {!isLoading && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px',marginBottom:'4px' }}>
                <S.StepSubtitle>
                  {dataCount} {t(trans.TEMPLATES_AVAILABLE.key, trans.TEMPLATES_AVAILABLE.value)}
                </S.StepSubtitle>
                {selectedTemplate && (
                  <p style={{ color: '#0092b8' }}>
                    {t(trans.TEMPLATE_SELECTED.key, trans.TEMPLATE_SELECTED.value)}{' '}
                    <Icon type="check" />
                  </p>
                )}
              </div>
              {templateChannels?.length < 2 && (
                <div style={{color: '#AD4E00'}}>
                  <Icon type="exclamation-circle" color="yellow" />
                  {` ${t(trans.MISSING_TEMPLATE_WARNING.key, { type: templateChannels.includes('document') ? 'Document' : 'Email', defaultValue: trans.MISSING_TEMPLATE_WARNING.value, })}.`}
                </div>
              )}
            </div>
          )}
          {isLoading ? (
            <LoaderContainer style={{ marginTop: '25%' }}>
              <Loader />
            </LoaderContainer>
          ) : (
            <div style={{ flex: 1, minHeight: 0, position: 'relative', marginBottom: '16px' }}>
              <ScrollContainerV2 hideScrollToTop={true} fetchMoreData={fetchMoreTemplates}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginTop: '4px',
                    paddingBottom: '16px',
                  }}
                >
                  {fetchedTemplates &&
                    fetchedTemplates.map(tData => (
                      <S.TemplateCard
                        key={tData.communicationId}
                        selected={selectedTemplate === tData.communicationId}
                        onClick={() => {
                          setSelectedTemplate(tData.communicationId);
                          if(jobType === 'ADVANCED' && format.toLowerCase().includes('multi')) {
                            saveMultiChannelTemplateInfo(tData.communicationId);
                          } else {
                            handleTemplateChange(tData);
                          }
                          setUploadedFile(null);
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600' }}>{tData.communicationName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {t(trans.CREATED_ON.key, trans.CREATED_ON.value)} {moment(tData.createdOn).format('YYYY-MM-DD')}
                          </div>
                        </div>
                        <S.CheckCircle selected={selectedTemplate === tData.communicationId}>
                          {selectedTemplate === tData.communicationId && (
                            <Icon type="check" style={{ color: '#fff', fontSize: '10px' }} />
                          )}
                        </S.CheckCircle>
                      </S.TemplateCard>
                    ))}
                  {!isLoading &&
                    isLoadingMore &&
                    fetchedTemplates &&
                    fetchedTemplates.length !== dataCount &&
                    dataCount >= 6 && (
                      <div className="addMore">
                        <Icon type="loading" />
                        {t(trans.LOADING_MORE.key, trans.LOADING_MORE.value)}
                      </div>
                    )}
                </div>
              </ScrollContainerV2>
            </div>
          )}
          <div
            style={{
              marginTop: 'auto',
              borderTop: '1px solid #f3f4f6',
              backgroundColor: '#ffffff',
              flexShrink: 0,
            }}
          >
            {!format.toLowerCase().includes('multi') && languageSelect()}
          </div> 
       </S.FormContainer>
      </S.StepContent>
    );
  }
};

export default Step2TemplateSelection;
